import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from './router';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ColorModeProvider } from './contexts/ColorModeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Stations from './pages/Stations';
import Categories from './pages/Categories';
import Advertising from './pages/Advertising';
import Settings from './pages/Settings';
import PlaylistManager from './pages/PlaylistManager';
import Scheduler from './pages/SchedulerEnhanced';
import Clocks from './pages/Clocks';
import Voicetrack from './pages/VoiceTrack';
import Library from './pages/Library';
import Live from './pages/Live';
import LiveGuest from './pages/LiveGuest';
import AIVoice from './pages/AIVoice';
import Logger from './pages/Logger';
import Intelligence from './pages/Intelligence';
import CartwallWindow from './pages/CartwallWindow';
import ChangePassword from './pages/ChangePassword';

function ProtectedRoute({ children, allowPasswordChange = false }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user must change password, redirect to change-password page
  if (user.mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  return children;
}

// Route guard that checks module-level permissions
function ModuleRoute({ children, module, chromeFree = false }) {
  const { user, isLoading, hasModuleAccess, getHourRestrictions, getServerLocalHour } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  const allowedHours = module ? getHourRestrictions(module) : null;
  // Match backend isPermissionHourAllowed (server local hour), not the browser TZ.
  const outsideAllowedHours = Array.isArray(allowedHours)
    && !allowedHours.map(Number).includes(
      typeof getServerLocalHour === 'function' ? getServerLocalHour() : new Date().getHours()
    );

  if (module && (!hasModuleAccess(module) || outsideAllowedHours)) {
    const denied = (
      <Box sx={{ textAlign: 'center', py: 8, px: 3, minHeight: chromeFree ? '100vh' : undefined }}>
        <Typography variant="h5" sx={{ color: 'secondary.main', mb: 2 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {outsideAllowedHours
            ? 'This module is not available for your account at this hour.'
            : 'You do not have permission to access this module.'}
        </Typography>
        <Button variant="outlined" onClick={() => window.history.back()} color="primary">
          Go Back
        </Button>
      </Box>
    );
    return chromeFree ? denied : <Layout>{denied}</Layout>;
  }

  return children;
}

// Superadmin-only route guard
function SuperAdminRoute({ children }) {
  const { user, isLoading, isSuperAdmin } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (!isSuperAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <ColorModeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute allowPasswordChange>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stations"
              element={
                <SuperAdminRoute>
                  <Layout>
                    <Stations />
                  </Layout>
                </SuperAdminRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ModuleRoute module="library">
                  <Layout>
                    <Categories />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/advertising"
              element={
                <ModuleRoute module="advertising">
                  <Layout>
                    <Advertising />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists"
              element={
                <ModuleRoute module="playlists">
                  <Layout>
                    <PlaylistManager />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/schedule"
              element={
                <ModuleRoute module="clocks">
                  <Layout>
                    <Clocks />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/schedule/advanced"
              element={
                <ModuleRoute module="clocks">
                  <Layout>
                    <Scheduler />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/clocks"
              element={
                <ModuleRoute module="clocks">
                  <Layout>
                    <Clocks />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/voicetrack"
              element={
                <ModuleRoute module="voicetrack">
                  <Layout>
                    <Voicetrack />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ModuleRoute module="library">
                  <Layout>
                    <Library />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/live"
              element={
                <ModuleRoute module="live" chromeFree>
                  <Live />
                </ModuleRoute>
              }
            />
            <Route path="/live/join/:token" element={<LiveGuest />} />
            <Route
              path="/ai"
              element={
                <ModuleRoute module="ai_content">
                  <Layout>
                    <AIVoice />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/logger"
              element={
                <ModuleRoute module="logger">
                  <Layout>
                    <Logger />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/intelligence"
              element={
                <ModuleRoute module="intelligence">
                  <Layout>
                    <Intelligence />
                  </Layout>
                </ModuleRoute>
              }
            />
            <Route
              path="/studio-output"
              element={
                <ModuleRoute module="studio_output">
                  <Navigate to="/settings?tab=studio_mode" replace />
                </ModuleRoute>
              }
            />
            <Route
              path="/cartwall/:id"
              element={
                <ModuleRoute module="live">
                  <CartwallWindow />
                </ModuleRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ColorModeProvider>
  );
}

export default App;
